// Types + presentation helpers for the market-timing signals served by the
// Worker. (The signal math itself lives server-side in worker/src/signals.ts.)

export type Conviction =
  | "building"
  | "softening"
  | "broad-rally"
  | "exhaustion"
  | "neutral";

export interface SeriesSignal {
  id: string;
  asset_class: "property" | "car";
  segment: string;
  source: string;
  unit: string;
  metric: string;
  as_of: string | null;
  latest_value: number | null;
  momentum_3m: number | null;
  momentum_12m: number | null;
  valuation_z: number | null;
  entry_score: number | null;
  verdict: "favourable" | "neutral" | "stretched" | null;
  // Volume / velocity (leading). Null on price-index / COE series that carry no
  // meaningful transaction count.
  vol_3m?: number | null;
  vol_12m?: number | null;
  vol_z?: number | null;
  conviction?: Conviction | null;
}

export interface SeriesDetail {
  meta: SeriesSignal;
  observations: { period: string; value: number; provisional?: boolean }[];
  signalHistory: {
    as_of: string;
    latest_value: number;
    momentum_3m: number | null;
    momentum_12m: number | null;
    valuation_z: number | null;
    entry_score: number;
    verdict: string;
    vol_3m?: number | null;
    vol_12m?: number | null;
    vol_z?: number | null;
    conviction?: Conviction | null;
  }[];
}

// Human-readable read of the price/volume divergence tag. `null` when a series
// has no volume signal (price-index / COE), so callers can skip it.
export function convictionText(c: Conviction | null | undefined): { label: string; hint: string } | null {
  switch (c) {
    case "building":
      return { label: "Demand building", hint: "Prices soft but transaction volume is rising — demand firming under a soft print; a dip may not last." };
    case "softening":
      return { label: "Genuine softening", hint: "Prices soft and volume falling too — real weakness, not a head-fake; patience can pay." };
    case "broad-rally":
      return { label: "Broad rally", hint: "Prices and volume both rising — a broad-based rally you'd be chasing, not a value window." };
    case "exhaustion":
      return { label: "Rally thinning", hint: "Prices rising on falling volume — the rally is losing participation and may stall." };
    case "neutral":
      return { label: "Volume steady", hint: "Price and volume broadly in step — no divergence signal either way." };
    default:
      return null;
  }
}

export function verdictColor(verdict: string | null): string {
  switch (verdict) {
    case "favourable":
      return "var(--good)";
    case "stretched":
      return "var(--bad)";
    default:
      return "var(--neutral)";
  }
}

export function verdictLabel(verdict: string | null): string {
  switch (verdict) {
    case "favourable":
      return "Favourable entry";
    case "stretched":
      return "Stretched";
    case "neutral":
      return "Neutral";
    default:
      return "No signal yet";
  }
}

export function fmtSGD(v: number | null, opts: { psf?: boolean } = {}): string {
  if (v === null || !Number.isFinite(v)) return "—";
  if (opts.psf) return `S$${Math.round(v).toLocaleString()} psf`;
  return `S$${Math.round(v).toLocaleString()}`;
}

/**
 * Format a MODEL ESTIMATE (fair value, buying power, a projected figure) at the
 * precision it deserves: S$ + 3 significant figures, compact (S$1.39M, S$842k).
 * Quoted/official figures (asking price, duties, user inputs) keep fmtSGD's full
 * digits. See DESIGN_SYSTEM.md §4.
 */
export function fmtEstimate(v: number | null): string {
  if (v === null || !Number.isFinite(v)) return "—";
  const a = Math.abs(v);
  const sign = v < 0 ? "−" : "";
  const sig3 = (x: number) => Number(x.toPrecision(3));
  if (a >= 1e6) return `${sign}S$${sig3(a / 1e6)}M`;
  if (a >= 1e4) return `${sign}S$${sig3(a / 1e3)}k`;
  if (a >= 1e3) return `${sign}S$${(Math.round(a / 10) * 10).toLocaleString()}`;
  return `${sign}S$${Math.round(a).toLocaleString()}`;
}

/** An estimated range at one consistent precision, e.g. "S$1.31M – S$1.46M". */
export function fmtRange(lo: number | null, hi: number | null): string {
  if (lo == null || hi == null || !Number.isFinite(lo) || !Number.isFinite(hi)) return "—";
  return `${fmtEstimate(lo)} – ${fmtEstimate(hi)}`;
}

export function fmtPct(v: number | null): string {
  if (v === null || !Number.isFinite(v)) return "—";
  const s = v >= 0 ? "+" : "";
  return `${s}${v.toFixed(1)}%`;
}

const SQM_PER_SQFT = 10.7639;
// Format a $psf value in the user's chosen basis: $psf or $ per square metre.
export function fmtPsf(psf: number | null, unit: "psf" | "psm" = "psf"): string {
  if (psf === null || !Number.isFinite(psf)) return "—";
  if (unit === "psm") return `S$${Math.round(psf * SQM_PER_SQFT).toLocaleString()}/m²`;
  return `S$${Math.round(psf).toLocaleString()} psf`;
}

// Format a series' latest value according to its metric.
export function fmtMetric(value: number | null, metric: string): string {
  if (value === null || !Number.isFinite(value)) return "—";
  if (metric === "price_index") return `Index ${Math.round(value)}`;
  if (metric === "median_psf" || metric === "district_psf" || metric === "new_sale_psf") return `S$${Math.round(value).toLocaleString()} psf`;
  return `S$${Math.round(value).toLocaleString()}`;
}

// Break the entry score into its three interpretable factors, mirroring the
// server's computeSignal weights/mappings, so the UI can show *why* a segment
// scores as it does. Each factor is 0..100 (higher = more favourable to enter).
export interface EntryFactors {
  valuation: number | null; // below-trend = high
  momentum12m: number | null; // falling/flat = high
  momentum3m: number | null;
  entry: number | null;
}
const clamp01 = (x: number) => Math.max(0, Math.min(100, x));
export function entryFactors(s: SeriesSignal): EntryFactors {
  const val = s.valuation_z == null ? null : clamp01(50 - s.valuation_z * 25);
  const m12 = s.momentum_12m == null ? null : clamp01(50 - s.momentum_12m * 4);
  const m3 = s.momentum_3m == null ? null : clamp01(50 - s.momentum_3m * 4);
  return { valuation: val, momentum12m: m12, momentum3m: m3, entry: s.entry_score };
}

// Price-index series are quarterly and unit-less, so treat them as market-timing
// only (no affordability comparison against a dollar buying power).
export function isTimingOnly(metric: string): boolean {
  return metric === "price_index" || metric === "coe_premium";
}
